import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import {
  Input,
  Select,
  OrgForm,
  Button,
  ButtonLink,
} from 'components/common/FormItems';
import {
  withTranslation,
  withDocumentCategory,
  withLanguage,
  withOrganisation,
  withMsgInfo,
} from 'components/hoc';
import {
  searchFormsThunk,
  addFavoriteFormsThunk,
  getFavoriteFormsThunk,
} from '../myFormsState/myFormsThunk';
import { clearSearchResults } from '../myFormsState/myFormsSlice';

const Wrapper = styled.div``;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const StyledInput = styled(Input)`
  padding-right: 0.5rem;
`;

const StyledSelect = styled(Select)`
  margin-right: 2rem;
`;

const StyledButton = styled(Button)`
  width: 11rem;
  height: 2.7rem;
  line-height: 1.25rem;
  font-weight: 500;
  font-size: 1rem;
  border: solid 1px #1d579b;
  background: #4c81be;
  color: #fff;
  transition: all ease-in-out 300ms;
  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.29);
  }
`;

const ResultsWrapper = styled.div`
  margin-top: 1.5rem;
`;

const ResultTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  color: var(--admincat-color-grey-7);
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  background: var(--admincat-color-grey-6);
  color: var(--admincat-color-grey-7);
  border-bottom: 2px solid var(--admincat-color-grey-5);
  font-weight: 600;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid var(--admincat-color-grey-5);
  color: var(--admincat-color-grey-7);
  vertical-align: middle;
`;

const CheckboxTd = styled(Td)`
  width: 40px;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--admincat-color-grey-7);
  font-size: 1rem;
`;

const STATUS_IDLE = 'IDLE';
const STATUS_SEARCHING = 'SEARCHING';
const STATUS_DONE = 'DONE';

const initialValues = {
  description: '',
  issuing_org: '',
  form_category: '',
  language: '',
  own_sign: '',
};

const ActionButtons = ({
  searchStatus,
  searchResults,
  selectedIds,
  addLoading,
  handleSearch,
  onProposeCreation,
}) => (
  <div className="d-flex justify-content-between">
    {searchStatus === STATUS_DONE && searchResults.length === 0 ? (
      <div className="d-flex" style={{ width: '100%' }}>
        <StyledButton
          name="Search"
          style={{ marginLeft: 'auto' }}
          onClick={handleSearch}
        />
        <div style={{ flex: 1 }} />
        <StyledButton
          style={{ marginRight: '2rem' }}
          name="Propose creation"
          onClick={onProposeCreation}
        />
      </div>
    ) : (
      <StyledButton
        name={selectedIds.length > 0 ? 'Add Favourite' : 'Search'}
        type="submit"
        isLoading={searchStatus === STATUS_SEARCHING || addLoading}
      />
    )}
  </div>
);

const SearchFormsPage = (props) => {
  const {
    tr,
    documentCategoryLocaleOptions,
    activeLanguageOptions,
    orgOptions,
    setMsgInfo,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { searchResults, searchLoading, addLoading } = useSelector(
    (state) => state.myForms
  );

  const [formData, setFormData] = useState({ ...initialValues });
  const [searchStatus, setSearchStatus] = useState(STATUS_IDLE);
  const [selectedIds, setSelectedIds] = useState([]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    setSearchStatus(STATUS_SEARCHING);
    setSelectedIds([]);
    await dispatch(searchFormsThunk(formData));
    setSearchStatus(STATUS_DONE);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddFavorites = async () => {
    if (selectedIds.length === 0) {
      Swal.fire(tr('Warning!'), tr('Please select at least one form.'), 'warning');
      return;
    }
    try {
      await dispatch(addFavoriteFormsThunk(selectedIds));
      dispatch(setMsgInfo({ success: true, msg: [tr('Forms added to favourites.')] }));
      dispatch(getFavoriteFormsThunk());
      dispatch(clearSearchResults());
      navigate('/dashboard/my-forms/list');
    } catch (_) {
      Swal.fire(tr('Error'), tr('Failed to add favorites. Please try again.'), 'error');
    }
  };

  const onProposeCreation = () =>
    navigate('/dashboard/my-forms/propose', { state: { prefill: formData } });

  const onSubmit = () => {
    if (selectedIds.length > 0) {
      handleAddFavorites();
    } else {
      handleSearch();
    }
  };

  return (
    <Wrapper className="form-wrapper">
      <div className="flex">
        <StyledButtonLink
          to="/dashboard/my-forms/list"
          className="option-btn"
          icon="angle double left"
          name={tr('All Entries')}
        />
      </div>

      <div className="row">
        <div
          className="col-7 ml-10 mt-30 container"
          style={{
            position: 'relative',
            background: 'var(--admincat-color-grey-1)',
          }}
        >
          <br />

          <OrgForm
            onSubmit={onSubmit}
            formData={formData}
            isLoading={searchStatus === STATUS_SEARCHING || addLoading}
            renderActionButtons={() => (
              <ActionButtons
                searchStatus={searchStatus}
                searchResults={searchResults}
                selectedIds={selectedIds}
                addLoading={addLoading}
                handleSearch={handleSearch}
                onProposeCreation={onProposeCreation}
              />
            )}
            title={tr('Forms Search')}
          >
            <StyledInput
              onChange={onChange}
              name="description"
              placeholder={tr('Description')}
              label={tr('Description')}
              value={formData.description}
            />
            <StyledSelect
              onChange={onChange}
              name="issuing_org"
              label={tr('Issuing Organization')}
              optionDefaultValue={tr('Choose Organization')}
              options={orgOptions}
              value={formData.issuing_org}
              deselectable
              sort={false}
            />
            <StyledSelect
              onChange={onChange}
              name="form_category"
              label={tr('Form Category')}
              optionDefaultValue={tr('Choose Form Category')}
              localeOptions={documentCategoryLocaleOptions}
              value={formData.form_category}
              deselectable
            />
            <StyledSelect
              onChange={onChange}
              name="language"
              label={tr('Language')}
              optionDefaultValue={tr('Choose Language')}
              options={activeLanguageOptions}
              value={formData.language}
              deselectable
            />
            <StyledInput
              onChange={onChange}
              name="own_sign"
              placeholder={tr('Own Sign')}
              label={tr('Own Sign')}
              value={formData.own_sign}
            />

            {searchStatus === STATUS_DONE && (
              <ResultsWrapper>
                <hr style={{ background: 'var(--admincat-color-grey-2)', marginRight: '2rem' }} />

                {searchResults.length > 0 ? (
                  <ResultTable>
                    <thead>
                      <tr>
                        <Th />
                        <Th>{tr('Description')}</Th>
                        <Th>{tr('Issuing Organization')}</Th>
                        <Th>{tr('Form Category')}</Th>
                        <Th>{tr('Language')}</Th>
                        <Th>{tr('Own Sign')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((form) => (
                        <tr
                          key={form.id}
                          style={{
                            cursor: 'pointer',
                            background: selectedIds.includes(form.id)
                              ? 'var(--admincat-color-grey-5)'
                              : 'transparent',
                          }}
                          onClick={() => toggleSelect(form.id)}
                        >
                          <CheckboxTd>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(form.id)}
                              onChange={() => toggleSelect(form.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </CheckboxTd>
                          <Td>{form.description || '-'}</Td>
                          <Td>{form.issuing_org_detail?.name || '-'}</Td>
                          <Td>
                            {form.form_category_detail?.long_description_eng || '-'}
                          </Td>
                          <Td>
                            {form.language_detail?.long_description_eng || '-'}
                          </Td>
                          <Td>{form.own_sign || '-'}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </ResultTable>
                ) : (
                  <div className="mb-4">
                    <EmptyMsg>
                      <strong>{tr('No forms found.')}</strong>
                      <div>
                        {tr(
                          'Update your search criteria or propose a new form.'
                        )}
                      </div>
                    </EmptyMsg>
                  </div>
                )}
              </ResultsWrapper>
            )}
          </OrgForm>
        </div>
      </div>
    </Wrapper>
  );
};

export default compose(
  withTranslation,
  withDocumentCategory,
  withLanguage,
  withOrganisation,
  withMsgInfo,
)(SearchFormsPage);
